import React from 'react';
import { User, Bed, Calendar } from 'lucide-react';
import { Room } from '../types';
import { format, isSameDay } from 'date-fns';
import { getStatusColor } from '../utils/dateUtils';
import { cn } from '../utils/cn';
import { useDashboardStore } from '../stores/dashboardStore';

interface WeeklyOccupancyGridProps {
  rooms: Room[];
  calendarDates: Date[];
  selectedRoomId?: string;
  onBookingSelected: (bookingId: number) => void;
  onEmptySpaceClicked: (roomId: string, date: Date) => void;
}

const WeeklyOccupancyGrid: React.FC<WeeklyOccupancyGridProps> = ({
  rooms,
  calendarDates,
  selectedRoomId,
  onBookingSelected,
  onEmptySpaceClicked,
}) => {
  const { allBookings } = useDashboardStore();
  
  // Use rooms directly since backend now sends unique rooms
  const sortedRooms = [...rooms].sort((a, b) => a.roomId.localeCompare(b.roomId));

  const isToday = (date: Date) => {
    const now = new Date();
    return isSameDay(date, now);
  };

  // Use rooms directly without property grouping

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="flex">
          {/* Room Column Header */}
          <div className="w-64 p-4 bg-gray-100 border-r border-gray-200">
            <h3 className="font-semibold text-gray-800">Rooms</h3>
          </div>
          
          {/* Date Headers */}
          <div className="flex-1 flex">
            {calendarDates.map((date, index) => (
              <div key={index} className="flex-1 p-2 text-center border-r border-gray-200 last:border-r-0 relative">
                <div className={cn(
                  "text-xs font-medium mb-1",
                  isToday(date) ? "text-blue-600" : "text-gray-600"
                )}>
                  {format(date, 'EEE')}
                </div>
                <div className={cn(
                  "text-sm font-bold",
                  isToday(date) ? "text-blue-700" : "text-gray-800"
                )}>
                  {format(date, 'MMM dd')}
                </div>
                {/* Current day indicator line */}
                {isToday(date) && (
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Grid Content */}
      <div>
        {sortedRooms.map((room) => {
          const isSelected = selectedRoomId === room.roomId;
          
          return (
            <div
              key={room.roomId}
              className={cn(
                "flex border-b border-gray-200 hover:bg-gray-50 transition-colors",
                isSelected && "bg-blue-50"
              )}
            >
              {/* Room Info */}
              <div className={cn(
                "w-64 p-4 border-r border-gray-200 flex items-center",
                isSelected && "bg-blue-50"
              )}>
                <div>
                  <div className="font-semibold text-gray-800">{room.roomId}</div>
                  <div className="text-sm text-gray-600">{room.roomType}</div>
                </div>
              </div>

              {/* Occupancy Row */}
              <div className="flex-1 relative h-20">
                {/* Current day indicator line */}
                {calendarDates.map((date, index) => {
                  if (isToday(date)) {
                    return (
                      <div
                        key={`indicator-${index}`}
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                        style={{ left: `${(index / calendarDates.length) * 100}%` }}
                      />
                    );
                  }
                  return null;
                })}

                {/* Empty space clickable areas */}
                {calendarDates.map((date, index) => {
                  const dayWidth = 100 / calendarDates.length;
                  const leftPercent = (index / calendarDates.length) * 100;
                  
                  // Check if there's a booking for this room on this date
                  const hasBooking = allBookings.some(booking => {
                    if (booking.roomId !== room.roomId) return false;
                    const checkInDate = new Date(booking.originalCheckinDate);
                    const checkOutDate = new Date(booking.originalCheckoutDate);
                    // Normalize dates to compare only date part (remove time component)
                    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const normalizedCheckIn = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate());
                    const normalizedCheckOut = new Date(checkOutDate.getFullYear(), checkOutDate.getMonth(), checkOutDate.getDate());
                    return normalizedDate >= normalizedCheckIn && normalizedDate <= normalizedCheckOut;
                  });

                  return (
                    <div
                      key={`empty-${index}`}
                      className="absolute top-0 bottom-0 cursor-pointer hover:bg-gray-100 transition-colors"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${dayWidth}%`
                      }}
                      onClick={() => {
                        if (!hasBooking) {
                          onEmptySpaceClicked(room.roomId, date);
                        }
                      }}
                    />
                  );
                })}
                
                {/* Booking bubbles */}
                {(() => {
                  // Get all bookings for this room
                  const roomBookings = allBookings.filter(booking => booking.roomId === room.roomId);
                  
                  const bookingRanges: any[] = [];
                  
                  // Process each booking for this room
                  roomBookings.forEach(booking => {
                    const checkInDate = new Date(booking.originalCheckinDate);
                    const checkOutDate = new Date(booking.originalCheckoutDate);
                    
                    // Find which calendar dates this booking spans
                    const overlappingDates: number[] = [];
                    
                    calendarDates.forEach((date, index) => {
                      // Check if this date falls within the booking period
                    // Normalize dates to compare only date part (remove time component)
                    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const normalizedCheckIn = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate());
                    const normalizedCheckOut = new Date(checkOutDate.getFullYear(), checkOutDate.getMonth(), checkOutDate.getDate());
                    // Include both check-in and check-out dates in the booking range
                    if (normalizedDate >= normalizedCheckIn && normalizedDate <= normalizedCheckOut) {
                        overlappingDates.push(index);
                    }
                    });
                    
                    if (overlappingDates.length > 0) {
                      const startIndex = Math.min(...overlappingDates);
                      const endIndex = Math.max(...overlappingDates);
                      
                      const bookingRange = {
                        startIndex: startIndex,
                        endIndex: endIndex,
                        status: booking.bookingStatus === 'checked_in' ? 'Checked-In' : 
                               booking.bookingStatus === 'confirmed' ? 'Occupied' :
                               booking.bookingStatus === 'checked_out' ? 'Checked-Out' : 'Occupied',
                        guestName: booking.guestName,
                        bookingSource: booking.bookingSource,
                        booking: booking,
                        extendsBefore: checkInDate < calendarDates[0],
                        extendsAfter: checkOutDate > calendarDates[calendarDates.length - 1]
                      };
                      
                      bookingRanges.push(bookingRange);
                    }
                  });
                  
                  return bookingRanges.map((booking, bookingIndex) => {
                    // Simple full-cell positioning - no cell splits
                    const dayWidth = 100 / calendarDates.length;
                    const startPercent = (booking.startIndex / calendarDates.length) * 100;
                    const endPercent = ((booking.endIndex + 1) / calendarDates.length) * 100;
                    const widthPercent = endPercent - startPercent;
                    
                    return (
                      <div
                        key={bookingIndex}
                        className={cn(
                          "absolute top-1 bottom-1 rounded-lg px-2 py-1 flex flex-col justify-start cursor-pointer hover:opacity-80 transition-opacity",
                          // Status-based colors
                          booking.status === 'Checked-In' && "bg-green-100 border border-green-200",
                          booking.status === 'Occupied' && "bg-blue-100 border border-blue-200",
                          booking.status === 'Checked-Out' && "bg-orange-100 border border-orange-200",
                          booking.status === 'Maintenance' && "bg-red-100 border border-red-200"
                        )}
                        style={{
                          left: `${startPercent}%`,
                          width: `${widthPercent}%`
                        }}
                        onClick={() => onBookingSelected(booking.booking.bookingId)}
                      >
                        <div className="flex items-center space-x-1 mb-0.5">
                          <User className={cn(
                            "w-2.5 h-2.5",
                            booking.status === 'Checked-In' && "text-green-700",
                            booking.status === 'Occupied' && "text-blue-700",
                            booking.status === 'Checked-Out' && "text-orange-700",
                            booking.status === 'Maintenance' && "text-red-700"
                          )} />
                          <Bed className={cn(
                            "w-2.5 h-2.5",
                            booking.status === 'Checked-In' && "text-green-700",
                            booking.status === 'Occupied' && "text-blue-700",
                            booking.status === 'Checked-Out' && "text-orange-700",
                            booking.status === 'Maintenance' && "text-red-700"
                          )} />
                        </div>
                        <div className={cn(
                          "text-xs font-semibold truncate leading-tight",
                          booking.status === 'Checked-In' && "text-green-800",
                          booking.status === 'Occupied' && "text-blue-800",
                          booking.status === 'Checked-Out' && "text-orange-800",
                          booking.status === 'Maintenance' && "text-red-800"
                        )}>
                          {booking.extendsBefore && "..."}
                          {booking.guestName || 'Guest'}
                          {booking.extendsAfter && "..."}
                        </div>
                        <div className={cn(
                          "text-xs leading-tight",
                          booking.status === 'Checked-In' && "text-green-600",
                          booking.status === 'Occupied' && "text-blue-600",
                          booking.status === 'Checked-Out' && "text-orange-600",
                          booking.status === 'Maintenance' && "text-red-600"
                        )}>
                          {booking.status === 'Checked-In' ? 'Checked In' : 
                           booking.status === 'Occupied' ? 'Confirmed' :
                           booking.status === 'Checked-Out' ? 'Checked Out' :
                           booking.status === 'Maintenance' ? 'Blocked' : booking.status}
                        </div>
                        {/* Booking Source */}
                        {booking.bookingSource && (() => {
                          // Get the appropriate icon based on booking source
                          const getBookingSourceIcon = (source: string) => {
                            const normalizedSource = source?.toLowerCase();
                            switch (normalizedSource) {
                              case 'makemytrip':
                                return '/images/icons/mmt-icon.png';
                              case 'booking.com':
                                return '/images/icons/booking-icon.png';
                              case 'goibibo':
                                return '/images/icons/goibibo-icon.png';
                              default:
                                return '/images/icons/booking-icon.png';
                            }
                          };
                          
                          return (
                            <div className={cn(
                              "flex items-center space-x-1 mt-0.5",
                              booking.status === 'Checked-In' && "text-green-700",
                              booking.status === 'Occupied' && "text-blue-700",
                              booking.status === 'Checked-Out' && "text-orange-700",
                              booking.status === 'Maintenance' && "text-red-700"
                            )}>
                              <img 
                                src={getBookingSourceIcon(booking.bookingSource)} 
                                alt={booking.bookingSource}
                                className="w-2.5 h-2.5 flex-shrink-0"
                              />
                              <span className="text-xs font-medium truncate leading-tight">
                                {(() => {
                                  const source = booking.bookingSource?.toLowerCase();
                                  switch (source) {
                                    case 'makemytrip':
                                      return 'MakeMyTrip';
                                    case 'booking.com':
                                      return 'Booking.com';
                                    case 'goibibo':
                                      return 'Goibibo';
                                    default:
                                      return booking.bookingSource;
                                  }
                                })()}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyOccupancyGrid;
